import React, { useEffect, useState } from 'react';
import Box from '~/components/Box';
import { Spin } from 'antd';
import dayGridPlugin from '@fullcalendar/daygrid';
import ptLocale from '@fullcalendar/core/locales/pt';
import { getEvents } from '~/services/hooks/events';
import { FullCalendar } from './styles';
import { useCheckAuth } from '~/components/Can/checkAuth';
import IsMobile from '~/services/mobileCheck';
import EventFullView from '../Events/view';
import errorHandler from '~/Utils/errorHandler';

export default function Dashboard() {
  const isMobile = IsMobile();

  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [formVisible, setFormVisible] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const checkAuth = useCheckAuth();

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = { params: { month, year } };
      const data = await getEvents(params, 'no-paginated-dash');
      setEvents(data);
    } catch (error) {
      console.error(error);
      errorHandler(error);
    }
    setLoading(false);
  };

  const handleEdit = async (event) => {
    if (checkAuth('@events/view')) {
      try {
        const fullEvent = events.find((e) => e.id === parseInt(event._def.publicId));
        const data = await getEvents('', fullEvent.id);
        setEditEvent(data);
        setFormVisible(true);
      } catch (error) {
        console.error(error);
        errorHandler(error);
      }
    }
  };

  const handleDatesSet = (info) => {
    setMonth(info.view.currentStart.getMonth() + 1);
    setYear(info.view.currentStart.getFullYear());
  };

  const CustomEvent = ({ event }) => {
    const { status } = event.extendedProps;
    const handleStatus = () => {
      switch (status) {
        case 0:
          return '#bee5eb';
        case 1:
          return '#ffc965';
        case 2:
          return '#c3e6cb';
        case 3:
          return '#f5c6cb';
        case 4:
          return '#c6c8ca';
        default:
          return '#ffffff';
      }
    };

    return (
      <div
        style={{
          backgroundColor: handleStatus(),
          padding: isMobile ? '2px' : '8px',
          borderRadius: '3px',
          border: '1px solid #ddd',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          fontSize: isMobile ? '0.7rem' : '0.9rem',
          color: '#333',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '100%',
        }}
      >
        <strong>{event.title}</strong>
        <br />
        <span style={{ fontSize: isMobile ? '0.6rem' : '0.8rem' }}>
          {' '}
          {new Date(event.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} /{' '}
          {new Date(event.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    );
  };

  useEffect(() => {
    fetchEvents();
  }, [month, year]);

  return (
    <Box>
      <div
        style={{
          display: 'flex',
          gap: '1.5rem',
          flexWrap: 'wrap',
          marginBottom: '1rem',
          fontSize: '0.9rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: 14, height: 14, backgroundColor: '#bee5eb', border: '1px solid #999' }} />
          <span>Em Preenchimento</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: 14, height: 14, backgroundColor: '#ffc965', border: '1px solid #999' }} />
          <span>Orçamento em Análise</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: 14, height: 14, backgroundColor: '#c3e6cb', border: '1px solid #999' }} />
          <span>Aprovado</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: 14, height: 14, backgroundColor: '#f5c6cb', border: '1px solid #999' }} />
          <span>Reprovado</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: 14, height: 14, backgroundColor: '#c6c8ca', border: '1px solid #999' }} />
          <span>Finalizado</span>
        </div>
      </div>
      <Spin spinning={loading}>
        <div
          style={{
            width: '100%',
            overflowX: 'auto',
            height: isMobile ? '70vh' : 'calc(100vh - 150px)',
            minWidth: isMobile ? '600px' : 'auto',
          }}
        >
          <FullCalendar
            plugins={[dayGridPlugin]}
            initialView="dayGridMonth"
            events={events?.map((event) => ({
              id: event.id,
              title: event.name,
              start: event.starts_at,
              end: event.ends_at,
              extendedProps: { status: event.status },
            }))}
            locale={ptLocale}
            datesSet={handleDatesSet}
            eventClick={(info) => handleEdit(info.event)}
            headerToolbar={{
              left: 'prev,next',
              center: 'title',
              right: 'today',
            }}
            eventContent={(arg) => <CustomEvent event={arg.event} />}
            titleFormat={{ month: 'long', year: 'numeric' }}
            dayHeaderClassNames="my-custom-day-header"
            dayCellClassNames="my-custom-day-cell"
            dayHeaderContent={(arg) => {
              let text = arg.text.replace('feira', '').trim();

              if (text.toLowerCase() === 'sex.' || text.toLowerCase() === 'sex') {
                text = 'sexta';
              }

              return <div style={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>{text}</div>;
            }}
            height="auto"
            contentHeight="auto"
            aspectRatio={isMobile ? 0.5 : 1.35}
          />
        </div>
      </Spin>
      <EventFullView
        visible={formVisible}
        event={editEvent}
        editFromDash
        onClose={() => {
          setFormVisible(false);
          setEditEvent(null);
          fetchEvents();
        }}
      />
    </Box>
  );
}
